import type { JSX } from "react";
import { useState } from "react";

import { formatUserName } from "@impero-com/common/utils/formatting";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import {
    ActionMenuProps,
    actionMenuStatus,
    Button as UiButton,
    emitNotification,
    InformationModal,
} from "@impero-com/ui";
import { AxiosError } from "axios";
import { useNavigate } from "react-router-dom";

import { AssignmentCount, UserViewDto, UserView } from "api/generated/model";
import { usePostUsersInvite } from "api/generated/users/users";
import IconDelete from "img/delete.svg";
import IconInvite from "img/send-invite.svg";

import { WebRoute } from "../../routes";

import { useDeleteUserMutation } from "./queries/useDeleteUserMutation";

const OUTSTANDING_ASSIGNMENTS_STATUS = 460;

type UserActionsPopup =
    | "DELETE"
    | "CANNOT_DELETE"
    | "INVITE"
    | "OUTSTANDING_CONTROLS_OR_CONTROL_RESULTS"
    | null;

interface UserActions {
    /** Data for `OrgApplicationBar`'s `actions` prop. */
    actions: ActionMenuProps;
    /** The confirmation dialog for the pending action, if any. Must be rendered. */
    dialog: JSX.Element | null;
}

/**
 * Whether the user has no assignments of their own, and so can be deleted.
 *
 * Only *direct* assignments block deletion — group-derived ones survive the user
 * leaving the group, so they're deliberately not counted here.
 */
export function canDeleteUser(assignmentCount: AssignmentCount): boolean {
    return !(
        assignmentCount.controlAssignments.direct > 0 ||
        assignmentCount.controlResults.direct > 0 ||
        assignmentCount.pushReports.responsible > 0
    );
}

/**
 * The Delete user / Send invite app bar actions and their dialogs.
 *
 */
export function useUserActions(
    userWithDetails: UserViewDto,
    isUserDeletionAllowed: boolean
): UserActions {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const {
        content: { resource: user, actions },
    } = userWithDetails;

    const deleteUser = useDeleteUserMutation();
    const inviteUser = usePostUsersInvite();

    const [popup, setPopup] = useState<UserActionsPopup>(null);

    const handleDelete = () => {
        void deleteUser
            .mutateAsync({ userId: user.id })
            .then(() => navigate(WebRoute.USERS))
            .catch((error) => {
                const isOutstanding =
                    error instanceof AxiosError &&
                    error.response?.status === OUTSTANDING_ASSIGNMENTS_STATUS;
                if (isOutstanding) {
                    setPopup("OUTSTANDING_CONTROLS_OR_CONTROL_RESULTS");
                }
            });
    };

    const handleInvite = () => {
        void inviteUser
            .mutateAsync({ data: [user.id] })
            .then(() => {
                setPopup(null);
                emitNotification("success", t("User_Invite_Sent"));
            })
            .catch(() => {
                // Error toast is handled by the mutation's default onError.
            });
    };

    return {
        actions: {
            id: "user_actions",
            actions: [
                {
                    id: "user_action_delete",
                    label: t("UserManagement_Action_Delete"),
                    icon: { svg: IconDelete },
                    status: actionMenuStatus(
                        actions.delete,
                        t("User_Delete_Forbidden")
                    ),
                    onClick: () =>
                        setPopup(
                            isUserDeletionAllowed ? "DELETE" : "CANNOT_DELETE"
                        ),
                },
                {
                    id: "user_action_invite",
                    label: t("UserManagement_Action_Invite"),
                    icon: { svg: IconInvite },
                    status: actionMenuStatus(
                        actions.invite,
                        t("User_Invite_Forbidden")
                    ),
                    onClick: () => setPopup("INVITE"),
                },
            ],
        },
        dialog: (
            <UserActionDialogs
                popup={popup}
                user={user}
                deleting={deleteUser.isPending}
                inviting={inviteUser.isPending}
                onClose={() => setPopup(null)}
                onConfirmDelete={handleDelete}
                onConfirmInvite={handleInvite}
            />
        ),
    };
}

interface UserActionDialogsProps {
    popup: UserActionsPopup;
    user: UserView;
    deleting: boolean;
    inviting: boolean;
    onClose: () => void;
    onConfirmDelete: () => void;
    onConfirmInvite: () => void;
}

function UserActionDialogs({
    popup,
    user,
    deleting,
    inviting,
    onClose,
    onConfirmDelete,
    onConfirmInvite,
}: UserActionDialogsProps) {
    const { t } = useTranslation();

    switch (popup) {
        case null:
            return null;
        case "DELETE":
            return (
                <InformationModal
                    open
                    onClose={onClose}
                    type="error"
                    icon="delete"
                    title={t("UserManagement_Action_Delete")}
                    footer={
                        <>
                            <UiButton onClick={onClose}>{t("Cancel")}</UiButton>
                            <UiButton
                                variant="destructive"
                                loading={deleting}
                                onClick={onConfirmDelete}
                            >
                                {t("Delete")}
                            </UiButton>
                        </>
                    }
                >
                    {t("UserManagement_DeleteBody", {
                        name: formatUserName(user),
                    })}
                </InformationModal>
            );
        case "CANNOT_DELETE":
            return (
                <InformationModal
                    open
                    onClose={onClose}
                    type="warning"
                    title={t("UserManagement_Action_CannotDelete")}
                    footer={
                        <UiButton variant="primary" onClick={onClose}>
                            {t("Close")}
                        </UiButton>
                    }
                >
                    {t("UserManagement_CannotDeleteBody_DirectAssignments")}
                </InformationModal>
            );
        case "OUTSTANDING_CONTROLS_OR_CONTROL_RESULTS":
            return (
                <InformationModal
                    open
                    onClose={onClose}
                    type="warning"
                    title={t("UserManagement_Action_CannotDelete")}
                    footer={
                        <UiButton variant="primary" onClick={onClose}>
                            {t("Close")}
                        </UiButton>
                    }
                >
                    {t("UserManagement_CannotDeleteUser_DirectAssignments")}
                </InformationModal>
            );
        case "INVITE":
            return (
                <InformationModal
                    open
                    onClose={onClose}
                    type="info"
                    icon="send"
                    title={t("UserManagement_Action_Invite")}
                    footer={
                        <>
                            <UiButton onClick={onClose}>{t("Cancel")}</UiButton>
                            <UiButton
                                variant="primary"
                                loading={inviting}
                                onClick={onConfirmInvite}
                            >
                                {t("UserManagement_Action_Invite")}
                            </UiButton>
                        </>
                    }
                >
                    {t("UserManagement_InviteBody", { username: user.email })}
                </InformationModal>
            );
        default:
            return assertNever(popup);
    }
}
